/**
 * Portfolio Cost & Savings — single integrated section.
 * Replaces separate Buildings Cost and Transportation Cost sections.
 * Tabs: All | Buildings | Transport. Summary cards + charts; green = savings, red = cost.
 */
import React, { useMemo, useState } from 'react';
import { PortfolioCostSummaryCards, type PortfolioMetrics } from './PortfolioCostSummaryCards';
import {
  PortfolioCumulativeNetCostChart,
  PortfolioAnnualCashFlowChart,
  PortfolioCostBreakdownChart,
} from './PortfolioCostCharts';
import {
  BuildingsAnnualCostSavingsChart,
  BuildingsCumulativeNetCostChart,
  BuildingsCostBreakdownChart,
  BuildingsCostPerTonChart,
  BuildingsUtilityBillChart,
  BuildingsElectricityVsGasChart,
} from './BuildingsCostCharts';
import { Line, Bar } from 'react-chartjs-2';
import type { BuildingsCostResults } from '../../services/buildingsCostModel';
import type { TransportCostResults } from '../../services/transportCostModel';
import type { PolicyScenario } from '../../services/policyImpactService';
import type { BuildingsBauScenario } from '../../services/buildingsBauPathways';
import { currencyShort, FINANCIAL_COLORS } from './portfolioCostUtils';
import { KeyAssumptionsTable } from './KeyAssumptionsTable';
import type { ScenarioCostOptions } from '../../services/scenarioCostModel';

export type PortfolioTab = 'all' | 'buildings' | 'transport';

export type PortfolioCostView = 'incremental_vs_bau' | 'bau_costs' | 'total_costs';

export interface PortfolioCostSectionProps {
  buildingsCostResults: BuildingsCostResults | null;
  transportCostResults: TransportCostResults | null;
  /** Optional population for cost per capita (e.g. from baseline). */
  population?: number | null;
  /** Location label for debug/context */
  location?: string;
  /** Policy scenario (for Key Assumptions and cost view label). */
  scenario?: PolicyScenario;
  /** Cost view: BAU (state policy alone), Local Policy (incremental), Total. */
  costView?: PortfolioCostView;
  onCostViewChange?: (view: PortfolioCostView) => void;
  emfacVersion?: '2021' | '2025';
  buildingsBauScenario?: BuildingsBauScenario;
  /** Optional cost options for Key Assumptions table. */
  costOptions?: ScenarioCostOptions;
  /** When true, section shows a compact header until the user expands. */
  defaultCollapsed?: boolean;
}

function buildPortfolioMetrics(
  buildings: BuildingsCostResults | null,
  transport: TransportCostResults | null,
  population: number | null
): PortfolioMetrics {
  const upfrontCost = (buildings?.costEffectivenessMetrics?.upfrontCapitalCost ?? 0) + (transport?.upfrontCapitalCost ?? 0);
  const npv = (buildings?.costEffectivenessMetrics?.netPresentValue ?? 0) + (transport?.netPresentValue ?? 0);
  const fuelSavings = (buildings?.annualSavingsSeries?.reduce((s, r) => s + r.value, 0) ?? 0) + (transport?.breakdown?.fuelSavings ?? 0);
  const emissionsReductionMT = (buildings?.totalBuildingEmissionsReductionMT ?? 0) + (transport?.emissionsReductionMT ?? 0);
  const totalCumulativeNetCost = (buildings?.costEffectivenessMetrics?.cumulativeNetCost ?? 0) + (transport?.cumulativeNetCost ?? 0);
  const costPerTon = emissionsReductionMT > 0 ? totalCumulativeNetCost / emissionsReductionMT : null;
  const costPerCapita = population != null && population > 0 ? totalCumulativeNetCost / population : null;

  // Portfolio payback: first year where combined cumulative ≤ 0 after we've seen positive combined (real investment to recover)
  let paybackYear: number | null = null;
  const bSeries = buildings?.cumulativeNetCostSeries ?? [];
  const tSeries = transport?.cumulativeNetCostSeries ?? [];
  const yearSet = new Set<number>();
  bSeries.forEach((r) => yearSet.add(r.year));
  tSeries.forEach((r) => yearSet.add(r.year));
  const years = Array.from(yearSet).sort((a, b) => a - b);
  const bByYear = new Map(bSeries.map((r) => [r.year, r.value]));
  const tByYear = new Map(tSeries.map((r) => [r.year, r.value]));
  const hasInvestment = (buildings?.costEffectivenessMetrics?.upfrontCapitalCost ?? 0) + (transport?.upfrontCapitalCost ?? 0) > 0;
  if (hasInvestment) {
    let seenPositive = false;
    for (const y of years) {
      const sum = (bByYear.get(y) ?? 0) + (tByYear.get(y) ?? 0);
      if (sum > 0) seenPositive = true;
      if (sum <= 0 && seenPositive) {
        paybackYear = y;
        break;
      }
    }
  }

  return {
    upfrontCost,
    npv,
    costPerTon,
    paybackYear,
    fuelSavings,
    emissionsReductionMT,
    costPerCapita,
  };
}

function buildBuildingsMetrics(buildings: BuildingsCostResults | null): PortfolioMetrics | null {
  if (!buildings?.costEffectivenessMetrics) return null;
  const m = buildings.costEffectivenessMetrics;
  const fuelSavings = buildings.annualSavingsSeries?.reduce((s, r) => s + r.value, 0) ?? 0;
  return {
    upfrontCost: m.upfrontCapitalCost,
    npv: m.netPresentValue,
    costPerTon: m.costPerTonCO2eReduced ?? null,
    paybackYear: m.paybackYear ?? null,
    fuelSavings,
    emissionsReductionMT: buildings.totalBuildingEmissionsReductionMT ?? 0,
    costPerCapita: null,
  };
}

function buildTransportMetrics(transport: TransportCostResults | null): PortfolioMetrics | null {
  if (!transport) return null;
  return {
    upfrontCost: transport.upfrontCapitalCost,
    npv: transport.netPresentValue,
    costPerTon: transport.costPerTon,
    paybackYear: transport.paybackYear,
    fuelSavings: transport.breakdown?.fuelSavings ?? 0,
    emissionsReductionMT: transport.emissionsReductionMT,
    costPerCapita: null,
  };
}

const tabStyle = (active: boolean): React.CSSProperties => ({
  padding: '8px 14px',
  border: 'none',
  borderBottom: active ? '2px solid #2563eb' : '2px solid transparent',
  background: 'none',
  cursor: 'pointer',
  fontWeight: active ? 600 : 500,
  color: active ? '#1e40af' : '#6b7280',
  fontSize: '0.9375rem',
});

const COST_VIEW_LABELS: Record<PortfolioCostView, string> = {
  incremental_vs_bau: 'Local Policy (costs above BAU)',
  bau_costs: 'BAU (state policy alone)',
  total_costs: 'Total (state + local vs flat)',
};

export const PortfolioCostSection: React.FC<PortfolioCostSectionProps> = ({
  buildingsCostResults,
  transportCostResults,
  population = null,
  location,
  scenario,
  costView = 'incremental_vs_bau',
  onCostViewChange,
  emfacVersion = '2025',
  buildingsBauScenario = 'current_policy',
  costOptions,
  defaultCollapsed = false,
}) => {
  const [tab, setTab] = useState<PortfolioTab>('all');
  /** Controlled open state — React 18 @types omit `defaultOpen` on `<details>`. */
  const [portfolioDetailsOpen, setPortfolioDetailsOpen] = useState(!defaultCollapsed);

  const portfolioMetrics = useMemo(
    () => buildPortfolioMetrics(buildingsCostResults, transportCostResults, population ?? null),
    [buildingsCostResults, transportCostResults, population]
  );
  const buildingsMetrics = useMemo(() => buildBuildingsMetrics(buildingsCostResults), [buildingsCostResults]);
  const transportMetrics = useMemo(() => buildTransportMetrics(transportCostResults), [transportCostResults]);

  const hasAnyCost = buildingsCostResults != null || transportCostResults != null;

  if (!hasAnyCost) return null;

  return (
    <section
      id="portfolio-cost-section"
      className="policy-portfolio-cost-section"
      style={{ marginTop: 32, paddingTop: 24, borderTop: '1px solid #e5e7eb' }}
    >
      <details
        open={portfolioDetailsOpen}
        onToggle={(e) => setPortfolioDetailsOpen(e.currentTarget.open)}
        style={{ border: '1px solid #e2e8f0', borderRadius: 8, background: '#fafafa' }}
      >
        <summary
          style={{ cursor: 'pointer', padding: '12px 16px', fontWeight: 600, fontSize: '1rem', listStyle: 'none' }}
        >
          <span style={{ marginRight: 8 }}>▶</span> Portfolio Cost &amp; Savings
        </summary>
        <div style={{ padding: '0 16px 16px', borderTop: '1px solid #e2e8f0' }}>
      <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: 12, marginBottom: 12 }}>
        Combined buildings and transportation financial impacts. Negative values = net savings (green); positive = net cost (red).
        Uses values from <strong>Cost Assumptions</strong> below.
      </p>

      {onCostViewChange && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <label style={{ fontSize: '0.875rem', fontWeight: 500 }}>Cost view:</label>
            <select
              value={costView}
              onChange={(e) => onCostViewChange(e.target.value as PortfolioCostView)}
              style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: '0.875rem', minWidth: 260 }}
            >
              <option value="bau_costs">{COST_VIEW_LABELS.bau_costs}</option>
              <option value="incremental_vs_bau">{COST_VIEW_LABELS.incremental_vs_bau}</option>
              <option value="total_costs">{COST_VIEW_LABELS.total_costs}</option>
            </select>
          </div>
          {costView === 'total_costs' && (
            <p
              style={{
                fontSize: '0.8125rem',
                color: '#92400e',
                background: '#fffbeb',
                border: '1px solid #fcd34d',
                borderRadius: 6,
                padding: '8px 10px',
                marginTop: 10,
                marginBottom: 0,
              }}
            >
              <strong>Total (state + local vs flat):</strong> transport uses a <strong>flat</strong> 2023-tech baseline (population-scaled)
              vs your scenario, and still applies your <strong>EV %</strong> slider against the BAU EV ramp—so you can see large transport
              fuel $ even when you only move building sliders. For “BAU emissions path + local buildings only,” use{' '}
              <strong>Local Policy (costs above BAU)</strong>.
            </p>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid #e5e7eb' }}>
        <button type="button" style={tabStyle(tab === 'all')} onClick={() => setTab('all')} aria-pressed={tab === 'all'}>
          All
        </button>
        <button type="button" style={tabStyle(tab === 'buildings')} onClick={() => setTab('buildings')} aria-pressed={tab === 'buildings'}>
          Buildings
        </button>
        <button type="button" style={tabStyle(tab === 'transport')} onClick={() => setTab('transport')} aria-pressed={tab === 'transport'}>
          Transport
        </button>
      </div>

      {tab === 'all' && (
        <>
          <PortfolioCostSummaryCards metrics={portfolioMetrics} population={population} />
          <PortfolioCumulativeNetCostChart buildings={buildingsCostResults} transport={transportCostResults} />
          <PortfolioAnnualCashFlowChart buildings={buildingsCostResults} transport={transportCostResults} />
          <PortfolioCostBreakdownChart buildings={buildingsCostResults} transport={transportCostResults} />
        </>
      )}

      {tab === 'buildings' && (
        <>
          {buildingsMetrics && <PortfolioCostSummaryCards metrics={buildingsMetrics} />}
          {!buildingsCostResults && <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>Loading buildings cost…</p>}
          {buildingsCostResults && (
            <>
              <p style={{ fontSize: '0.8125rem', color: '#6b7280', marginTop: 12, marginBottom: 12 }}>
                <strong>Utility savings</strong> = gas + building electricity (no EV charging). Buildings and transport are separate; EV adoption does not affect building costs. In the GHG chart, the electricity sector includes EV charging for transport cost consistency.
              </p>
              <p style={{ fontSize: '0.8125rem', color: '#6b7280', marginTop: 4, marginBottom: 12 }}>
                <strong>Fuel prices &amp; tech costs</strong> come from the <strong>Cost Assumptions</strong> section below.
              </p>
              <p style={{ fontSize: '0.8125rem', color: '#6b7280', marginTop: 4, marginBottom: 12 }}>
                Simplified assumptions: flat average fuel prices; demand response as kWh reduction; panel upgrade and per-home installed costs not fully modeled.
              </p>
              <BuildingsAnnualCostSavingsChart costResults={buildingsCostResults} />
              <BuildingsUtilityBillChart costResults={buildingsCostResults} />
              <BuildingsElectricityVsGasChart costResults={buildingsCostResults} />
              <BuildingsCumulativeNetCostChart costResults={buildingsCostResults} />
              <BuildingsCostBreakdownChart costResults={buildingsCostResults} />
              <BuildingsCostPerTonChart metrics={buildingsCostResults.costEffectivenessMetrics} />
            </>
          )}
        </>
      )}

      {tab === 'transport' && (
        <>
          {transportMetrics && <PortfolioCostSummaryCards metrics={transportMetrics} />}
          {!transportCostResults && <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>Loading transport cost…</p>}
          {transportCostResults && (
            <>
              <p style={{ fontSize: '0.8125rem', color: '#6b7280', marginTop: 12, marginBottom: 12 }}>
                <strong>Fuel savings</strong> = gasoline saved − extra electricity for EV charging. EV adoption increases electricity gradually; net savings when gasoline savings exceed charging cost. EV charging kWh comes from the same source as the GHG forecast (electricity sector). Negative NPV = net savings.
              </p>
              <TransportCostCharts transport={transportCostResults} />
            </>
          )}
        </>
      )}

      {location && scenario && (
        <details style={{ marginTop: 24, padding: 12, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
          <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: '0.9375rem' }}>Key assumptions (BAU) by scenario</summary>
          <p style={{ fontSize: '0.8125rem', color: '#6b7280', marginTop: 8, marginBottom: 12 }}>
            BAU upfront costs and key inputs across EMFAC 2021 | 2025 × Conservative | Current Policy | Accelerated.
          </p>
          <KeyAssumptionsTable
            location={location}
            scenario={scenario}
            enabled={!!buildingsCostResults || !!transportCostResults}
            options={{ ...costOptions, emfacVersion, buildingsBauScenario }}
            embedded
          />
        </details>
      )}
        </div>
      </details>
    </section>
  );
};

const axisTick = (tickValue: string | number): string | number =>
  typeof tickValue === 'number' ? tickValue.toLocaleString(undefined, { maximumFractionDigits: 0 }) : tickValue;

/** Transport-only charts: annual fuel savings and cumulative net cost. */
const TransportCostCharts: React.FC<{ transport: TransportCostResults }> = ({ transport }) => {
  const labels = transport.cumulativeNetCostSeries?.map((r) => String(r.year)) ?? [];
  const cumulativeNetCost = transport.cumulativeNetCostSeries?.map((r) => r.value) ?? [];
  const annualSavings = transport.annualFuelSavingsSeries?.map((r) => r.value) ?? [];

  if (labels.length === 0) {
    return (
      <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: 16 }}>No transport time series available.</p>
    );
  }

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ marginBottom: 24 }}>
        <h5 style={{ marginBottom: 8 }}>Transport cumulative net cost</h5>
        <p style={{ fontSize: '0.8125rem', color: FINANCIAL_COLORS.muted, marginBottom: 8 }}>Capital exp. − fuel savings over time. Negative = net savings.</p>
        <div style={{ height: 240 }}>
          <Line
            data={{
              labels,
              datasets: [
                {
                  label: 'Cumulative net cost',
                  data: cumulativeNetCost,
                  borderColor: 'rgb(124, 58, 237)',
                  backgroundColor: 'rgba(124, 58, 237, 0.1)',
                  fill: true,
                  tension: 0.2,
                  pointRadius: 2,
                },
              ],
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { position: 'bottom' },
                tooltip: {
                  callbacks: {
                    label: (ctx) =>
                      `${ctx.dataset?.label ?? 'Value'}: ${currencyShort(Number(ctx.parsed?.y ?? 0))} (negative = net savings)`,
                  },
                },
              },
              scales: {
                x: { title: { display: true, text: 'Year' } },
                y: { ticks: { callback: axisTick } },
              },
            }}
          />
        </div>
      </div>
      <div>
        <h5 style={{ marginBottom: 8 }}>Annual fuel savings</h5>
        <p style={{ fontSize: '0.8125rem', color: FINANCIAL_COLORS.muted, marginBottom: 8 }}>Annual fuel cost savings (gasoline − electricity) by year.</p>
        <div style={{ height: 220 }}>
          <Bar
            data={{
              labels,
              datasets: [
                {
                  label: 'Annual fuel savings',
                  data: annualSavings,
                  backgroundColor: annualSavings.map((v) => (v >= 0 ? 'rgba(15, 159, 110, 0.7)' : 'rgba(199, 91, 57, 0.7)')),
                },
              ],
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { position: 'bottom' },
                tooltip: {
                  callbacks: {
                    label: (ctx) =>
                      `${ctx.dataset?.label ?? 'Value'}: ${currencyShort(Number(ctx.parsed?.y ?? 0))}`,
                  },
                },
              },
              scales: {
                x: { title: { display: true, text: 'Year' } },
                y: { ticks: { callback: axisTick } },
              },
            }}
          />
        </div>
      </div>
      {transport.breakdown && (
        <div style={{ marginTop: 16, padding: 12, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
          <h5 style={{ marginBottom: 8 }}>Transport breakdown (cumulative)</h5>
          <p style={{ fontSize: '0.8125rem', color: FINANCIAL_COLORS.muted }}>
            Gasoline savings: {currencyShort(transport.breakdown.gasolineSavings)} · Electricity cost increase: {currencyShort(transport.breakdown.electricityCostIncrease)} · Net fuel savings: {currencyShort(transport.breakdown.fuelSavings)} (negative = net savings).
          </p>
        </div>
      )}
    </div>
  );
};

export default PortfolioCostSection;
